import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Button, notification, Badge, Grid, Drawer } from 'antd';
import { apiFetch } from './api';
import { CommentOutlined, SettingOutlined, TeamOutlined, LogoutOutlined, ShopOutlined, BarChartOutlined, BellOutlined, HeartOutlined, ApiOutlined, MenuOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';

import CommentsList from './pages/CommentsList';
import RulesList from './pages/RulesList';
import RoutingList from './pages/RoutingList';
import BrandsList from './pages/BrandsList';
import Stats from './pages/Stats';
import Health from './pages/Health';
import Settings from './pages/Settings';
import Login from './pages/Login';

const { Header, Content, Footer, Sider } = Layout;
const { useBreakpoint } = Grid;

const APP_NAME = 'Social Media Comment Monitor';
const PAGE_TITLES: Record<string, string> = {
  '/': 'Комментарии',
  '/rules': 'Правила',
  '/routing': 'Ответственные',
  '/brands': 'Бренды',
  '/stats': 'Статистика',
  '/health': 'Состояние',
  '/settings': 'Настройки',
  '/login': 'Вход',
};

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.lg; // < 992px
  const [authenticated, setAuthenticated] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false); // сворачивание сайдбара на десктопе
  const [drawerOpen, setDrawerOpen] = useState(false); // выдвижное меню на мобильном
  const lastTotalRef = useRef<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setAuthenticated(!!token);
  }, []);

  // При переходе на десктоп закрываем мобильное меню.
  useEffect(() => { if (!isMobile) setDrawerOpen(false); }, [isMobile]);

  // Заголовок вкладки: «Раздел — Social Media Comment Monitor».
  useEffect(() => {
    const page = PAGE_TITLES[location.pathname];
    document.title = page ? `${page} — ${APP_NAME}` : APP_NAME;
  }, [location.pathname]);

  // Логотип из настроек.
  useEffect(() => {
    if (!authenticated) return;
    apiFetch<{ logo_url?: string }>('/settings/')
      .then((s) => setLogoUrl(s.logo_url || null))
      .catch(() => {});
  }, [authenticated]);

  // Живое обновление логотипа при сохранении на странице «Настройки».
  useEffect(() => {
    const onLogo = (e: Event) => setLogoUrl((e as CustomEvent).detail || null);
    window.addEventListener('logo-updated', onLogo);
    return () => window.removeEventListener('logo-updated', onLogo);
  }, []);

  // Опрос на предмет новых комментариев + всплывающее уведомление.
  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    const poll = async () => {
      try {
        const { total } = await apiFetch<{ total: number }>('/comments/count');
        if (!active) return;
        if (lastTotalRef.current !== null && total > lastTotalRef.current) {
          const delta = total - lastTotalRef.current;
          notification.info({
            title: 'Новые комментарии',
            description: `Получено новых: ${delta}. Нажмите, чтобы открыть.`,
            placement: 'topRight',
            duration: 6,
            onClick: () => navigate('/'),
            style: { cursor: 'pointer' },
          });
        }
        lastTotalRef.current = total;
      } catch (e) {
        /* 401 и прочее обрабатываются в apiFetch */
      }
      try {
        const a = await apiFetch<{ count: number }>('/system/alerts');
        if (active) setAlertCount(a.count);
      } catch (e) { /* ignore */ }
    };
    poll(); // базовое значение сразу (без уведомления)
    const id = setInterval(poll, 45000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [authenticated, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setAuthenticated(false);
    navigate('/login');
  };

  if (!authenticated) {
    return <Login onLogin={() => setAuthenticated(true)} />;
  }

  const go = (path: string) => {
    navigate(path);
    if (isMobile) setDrawerOpen(false); // на мобиле закрываем выдвижное меню после выбора
  };

  const logoNode = (expanded: boolean) => (
    <div style={{ height: 48, margin: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {logoUrl ? (
        <img src={logoUrl} alt="logo" style={{ maxWidth: '100%', maxHeight: 44, objectFit: 'contain' }} />
      ) : (
        <span style={{ color: '#fff', fontWeight: 600, whiteSpace: 'nowrap' }}>{expanded ? 'Монитор' : 'SM'}</span>
      )}
    </div>
  );

  const menuNode = (
    <Menu
      theme="dark"
      mode="inline"
      defaultSelectedKeys={['1']}
      onClick={({ key }) => {
        const map: Record<string, string> = { '1': '/', '2': '/rules', '3': '/routing', '4': '/brands', '5': '/stats', '6': '/health', '7': '/settings' };
        go(map[key]);
      }}
      items={[
        { key: '1', icon: <CommentOutlined />, label: 'Комментарии' },
        { key: '2', icon: <SettingOutlined />, label: 'Правила' },
        { key: '3', icon: <TeamOutlined />, label: 'Ответственные' },
        { key: '4', icon: <ShopOutlined />, label: 'Бренды' },
        { key: '5', icon: <BarChartOutlined />, label: 'Статистика' },
        { key: '6', icon: <HeartOutlined />, label: 'Состояние' },
        { key: '7', icon: <ApiOutlined />, label: 'Настройки' },
      ]}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Десктоп: обычный сворачиваемый сайдбар */}
      {!isMobile && (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} trigger={null}>
          {logoNode(!collapsed)}
          {menuNode}
        </Sider>
      )}
      {/* Мобильный: выдвижное меню (Drawer) */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={220}
          closable={false}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {logoNode(true)}
          {menuNode}
        </Drawer>
      )}
      <Layout>
        <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <Button type="text" icon={<MenuOutlined />} onClick={() => (isMobile ? setDrawerOpen((o) => !o) : setCollapsed((c) => !c))} title="Меню" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Badge count={alertCount} size="small">
              <Button icon={<BellOutlined />} onClick={() => go('/health')} title="Проблемы и состояние" />
            </Badge>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>Выйти</Button>
          </div>
        </Header>
        <Content style={{ margin: isMobile ? 8 : 16 }}>
          <Routes>
            <Route path="/" element={<CommentsList />} />
            <Route path="/rules" element={<RulesList />} />
            <Route path="/routing" element={<RoutingList />} />
            <Route path="/brands" element={<BrandsList />} />
            <Route path="/stats" element={<Stats />} />
            <Route path="/health" element={<Health />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </Content>
        <Footer style={{ textAlign: 'center' }}>Social Media Comment Monitor</Footer>
      </Layout>
    </Layout>
  );
};

export default App;